import { capture } from '@/handlers/error';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { ResizeBorder } from '@/pages/LessonEditor/components/ResizeBorder';
import { useComponentEditor, useSlideEditor } from '@/providers/editor/EditorProvider';
import { Coordinate, isSlideComponentCharacter, PropsWithPreview, Size, SlideComponent } from '@/types/slide';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FlipIcon from '@mui/icons-material/Flip';
import ImageIcon from '@mui/icons-material/Image';
import { Box } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'rooks';
import { ComponentLabel } from '../../ComponentLabel';
import { ContextualBar } from '../../ContextualBar';
import { useRightClickMenu } from '../../hooks/useRightClickMenu';
import { useSlideComponent } from '../../hooks/useSlideComponent';
import { RightClickMenu } from '../../RightClickMenu';
import { SlideRef } from '../../Slide';
import { CharacterPicker } from './CharacterPicker';

export interface SlideComponentCharacterProps {
    ref: React.RefObject<HTMLDivElement | null>;
    init: {
        model: SlideComponent;
        focus?: boolean;
    };
    slideRef: React.RefObject<SlideRef | null>;
}

export const SlideComponentCharacter: React.FC<PropsWithPreview<SlideComponentCharacterProps>> = ({ ref, init, preview, slideRef }) => {
    const { t } = useTranslation();
    const notifications = useNotifications();
    const { selectedSlide } = useSlideEditor();
    const { deleteComponent, updateComponent, createSlideImage, bringToFront, bringToBack, copyComponent, pasteComponent, undo, redo } = useComponentEditor();

    const { rightClickMenu, handleRightClickMenu, rightClickMenuClose } = useRightClickMenu();

    // Add refs for the popovers
    const imagePickerRef = useRef<HTMLDivElement | null>(null);

    const { isSelected } = useSlideComponent(ref, [], !!preview);

    const [model, setModel] = useState<SlideComponent>({ ...init.model });
    const [isFocus, setIsFocus] = useState<boolean>(init.focus ?? false);

    const [tSize, setTSize] = useState<Size>(model.size);
    const [tPos, setTPos] = useState<Coordinate>(model.pos);

    // Add state for char picker popover
    const [charPickerAnchorEl, setCharPickerAnchorEl] = useState<HTMLElement | null>(null);

    const [aspectRatio, setAspectRatio] = useState(model.size.width / model.size.height);

    const isEditing = isFocus || isSelected;

    const onDeleteComponent = React.useCallback(
        async (id: string) => {
            try {
                await deleteComponent(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [deleteComponent, notifications],
    );

    const onUpdateComponent = React.useCallback(
        async (id: string, component: SlideComponent) => {
            try {
                await updateComponent(id, component);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [updateComponent, notifications],
    );

    const onBringToFrontHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToFront(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToFront, notifications],
    );

    const onBringToBackHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToBack(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToBack, notifications],
    );

    const onUploadImage = React.useCallback(
        async (file: File): Promise<string> => {
            try {
                return await createSlideImage(selectedSlide.editorData?.id ?? '', file);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
                return '';
            }
        },
        [createSlideImage, notifications, selectedSlide.editorData?.id],
    );

    const debouncedUpdateComponent = useDebounce(onUpdateComponent, 500);

    useOutsideClick(ref, () => {
        if (preview) return;
        if (!ref) return;

        setIsFocus(false);
    });

    useEffect(() => {
        if (isFocus && !preview && slideRef.current?.containerRef.current) {
            setCharPickerAnchorEl(slideRef.current.containerRef.current);
        }
    }, [slideRef, isFocus, preview]);

    if (!isSlideComponentCharacter(model)) {
        console.error(`[SlideComponentCharacter] ${JSON.stringify(model)} is not SlideComponentCharacter model`);
        return null;
    }

    const isPlaceholder = !model.content.imageURL && !model.content.image;

    return (
        <Box
            onContextMenu={isEditing ? handleRightClickMenu : undefined}
            ref={ref}
            data-testid='slide-component-character-legacy'
            sx={{
                position: 'absolute',
                left: tPos.x,
                top: tPos.y,
                m: 'inherit',
                p: 'inherit',
                textAlign: 'left',
                width: tSize.width,
                height: tSize.height,
            }}
            onClick={(e) => {
                if (!preview) {
                    e.stopPropagation();
                }
            }}
            onDoubleClick={() => {
                if (preview) return;

                if (slideRef.current?.containerRef.current) {
                    setCharPickerAnchorEl(slideRef.current.containerRef.current);
                }
            }}
        >
            <ResizeBorder
                size={tSize}
                pos={tPos}
                preview={preview}
                isSelected={isEditing}
                onResize={(size, pos) => {
                    setTSize(size);
                    setTPos(pos);
                    const updateModel = { ...model, size, pos };
                    setModel({ ...updateModel });
                    debouncedUpdateComponent?.(model.id, { ...updateModel });
                }}
                aspectRatio={aspectRatio}
                slideRef={slideRef}
            >
                {!preview && (
                    <ComponentLabel
                        variant='caption'
                        sx={{
                            ...(!isEditing && {
                                opacity: 0, // Hide by default
                                transition: 'opacity 0.2s ease',
                                '#character-component:hover &': {
                                    opacity: 1, // Show when text-component is hovered
                                },
                            }),
                        }}
                        noWrap
                    >
                        {t('slide.legacy.character.title')}
                    </ComponentLabel>
                )}

                <Box
                    sx={{
                        m: 'inherit',
                        p: 'inherit',
                        width: '100%',
                        height: '100%',
                        aspectRatio: 'auto',
                        transform: `scaleX(${model.content.flip ?? 1})`,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    {isPlaceholder ? (
                        <Box
                            sx={{
                                backgroundColor: (theme) => (theme.vars || theme).palette.grey[200],
                                width: '100%',
                                height: '100%',
                                aspectRatio: 'auto',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <ImageIcon sx={{ color: (theme) => (theme.vars || theme).palette.grey[400], width: '60%', height: '60%' }} />
                        </Box>
                    ) : (
                        <Box
                            component='img'
                            src={model.content.imageURL ? model.content.imageURL : `/asset/characters/${model.content.image}/image.png`}
                            sx={{
                                width: '100%',
                                height: '100%',
                                aspectRatio: 'auto',
                            }}
                            width={model.size.width}
                        />
                    )}
                </Box>

                {!preview && isEditing && (
                    <ContextualBar
                        startButtons={[
                            {
                                icon: <EditIcon />,
                                tooltip: t('slide.legacy.character.tooltip.edit'),
                                onClick: () => {
                                    if (slideRef.current?.containerRef.current) {
                                        setCharPickerAnchorEl(slideRef.current.containerRef.current);
                                    }
                                },
                            },
                            {
                                icon: <FlipIcon />,
                                tooltip: t('slide.legacy.character.tooltip.flip'),
                                onClick: () => {
                                    const updatedContent = { ...model.content, flip: model.content.flip === 1 ? -1 : 1 };
                                    const updateModel = { ...model, content: updatedContent };
                                    setModel(updateModel);
                                    onUpdateComponent?.(model.id, updateModel);
                                },
                            },
                        ]}
                        endButtons={[
                            {
                                icon: <DeleteIcon />,
                                tooltip: t('slide.legacy.character.tooltip.remove'),
                                onClick: () => {
                                    onDeleteComponent?.(model.id);
                                },
                            },
                        ]}
                    />
                )}
            </ResizeBorder>

            {charPickerAnchorEl && (
                <CharacterPicker
                    anchorEl={charPickerAnchorEl}
                    slotProps={{
                        paper: {
                            ref: imagePickerRef, // Attach the ref to the Paper element
                        },
                    }}
                    value={{ content: model.content, size: model.size, pos: model.pos }}
                    onClose={() => {
                        setCharPickerAnchorEl(null);
                        setIsFocus(false);
                    }}
                    onUpdate={(settings) => {
                        const updatedContent = { ...model.content, ...settings.content };
                        const updateModel = { ...model, content: updatedContent, ...(settings.size ? { size: settings.size } : {}), ...(settings.pos ? { pos: settings.pos } : {}) };

                        setTPos(updateModel.pos);
                        setTSize(updateModel.size);
                        setAspectRatio(updateModel.size.width / updateModel.size.height);
                        setModel(updateModel);
                        onUpdateComponent?.(model.id, updateModel);
                    }}
                    onUpload={onUploadImage}
                />
            )}

            <RightClickMenu
                componentId={model.id}
                contextMenu={rightClickMenu}
                handleClose={rightClickMenuClose}
                bringToFront={onBringToFrontHandler}
                bringToBack={onBringToBackHandler}
                copyComponent={copyComponent}
                pasteComponent={pasteComponent}
                undo={undo}
                redo={redo}
            />
        </Box>
    );
};
